/**
 * Release Row Menu
 *
 * @module features/releases/components/ReleaseRowMenu
 * @description The kebab on every timeline row. Holds "Claim in <env>" (one
 * item per sandbox environment of the application) when the row has a commit
 * to claim, and the run's own action (Cancel or Delete) on release bands.
 * Revealed on row hover/focus, like the original band menu.
 */

import React from 'react';
import { Button, Menu, MenuItem, MenuSeparator, MenuTrigger } from '@chatsdet/ui';
import { DotsThreeVertical, Plus, Trash, XCircle } from '@phosphor-icons/react';
import { useClaimFeedback, useClaimSandboxV2 } from '../../../hooks/useClaimSandboxV2';

/** The application a row can claim a sandbox of, with its sandbox environments. */
export type ClaimTarget = {
  applicationId: string;
  applicationName: string;
  /** `owner/name` of a GitHub application, whose commits of any other repo are not claimable; `null` for a pushed-image application. */
  repo: string | null;
  environments: Array<{ id: string; name: string; environment: string }>;
};

type Props = {
  /**
   * The version this row claims: a commit sha (with the repo it is in, `null` when unknown) or a push id (repo
   * `null`); `null` when the row has none. `disabledReason` keeps the items but disables them, saying why.
   */
  claimVersion: { version: string; repoFullName: string | null; disabledReason?: string } | null;
  claimTarget: ClaimTarget | null;
  /** The release run's own action, on bands only. */
  runAction?: { kind: 'cancel' | 'delete'; onAction: () => void };
};

export function ReleaseRowMenu({ claimVersion, claimTarget, runAction }: Props) {
  const claim = useClaimSandboxV2();
  const feedback = useClaimFeedback(claimTarget?.applicationName ?? '');
  // A multi-repo property can show another repo's commits; the application builds only its own repo. A commit with
  // no repo (rows from before the multi-repo backfill) belongs to the primary repo.
  const claimable =
    claimVersion &&
    claimTarget &&
    (claimVersion.repoFullName === null || claimVersion.repoFullName === claimTarget.repo);
  const claimedVersion = claimable ? claimVersion.version : null;
  const claimDisabledReason = claimable ? claimVersion.disabledReason : undefined;
  const claimEnvironments = claimable ? claimTarget.environments : [];

  if (claimEnvironments.length === 0 && !runAction) return null;

  const handleAction = (key: React.Key) => {
    const id = String(key);
    if (id === 'run-action') {
      runAction?.onAction();
      return;
    }
    const environment = claimEnvironments.find((env) => `claim:${env.id}` === id);
    if (!environment || !claimedVersion || !claimTarget) return;
    claim.mutate(
      { applicationId: claimTarget.applicationId, environment: environment.environment, version: claimedVersion },
      feedback
    );
  };

  return (
    <div className="pt-1 opacity-0 transition-opacity group-hover/run:opacity-100 group-focus-within/run:opacity-100">
      <MenuTrigger>
        <Button variant="icon" size="sm" iconOnly tooltip={runAction ? 'Run actions' : 'Row actions'}>
          <DotsThreeVertical size={16} weight="bold" />
        </Button>
        <Menu onAction={handleAction}>
          {claimEnvironments.map((env) => (
            <MenuItem key={env.id} id={`claim:${env.id}`} isDisabled={claim.isPending || claimDisabledReason !== undefined}>
              <Plus size={14} className="mr-2" />
              Claim in {env.name}
              {claimDisabledReason ? <span className="ml-2 text-muted-foreground">{claimDisabledReason}</span> : null}
            </MenuItem>
          ))}
          {claimEnvironments.length > 0 && runAction ? <MenuSeparator /> : null}
          {runAction ? (
            <MenuItem id="run-action" destructive>
              {runAction.kind === 'cancel' ? (
                <XCircle size={14} className="mr-2" />
              ) : (
                <Trash size={14} className="mr-2" />
              )}
              {runAction.kind === 'cancel' ? 'Cancel run' : 'Delete run'}
            </MenuItem>
          ) : null}
        </Menu>
      </MenuTrigger>
    </div>
  );
}
